import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import api from '../../utils/api';
import { toast } from 'sonner';
import { HardDrive, MemoryStick, Network, RefreshCw, Loader2, Activity, PieChart, Info } from 'lucide-react';
import HardwareDetailsSection from './HardwareDetailsSection';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

const InfoTab = ({ agentId }) => {
  const [activeSection, setActiveSection] = useState('hardware');
  const [loading, setLoading] = useState(false);
  const [cpuData, setCpuData] = useState(null);
  const [diskData, setDiskData] = useState(null);
  const [memoryData, setMemoryData] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [hardwareDetails, setHardwareDetails] = useState(null);

  useEffect(() => {
    loadHardwareDetails();
    loadSectionData(activeSection);
  }, [agentId, activeSection]);

  const loadHardwareDetails = async () => {
    try {
      const res = await api.get(`/agents/${agentId}`);
      setHardwareDetails(res.data.system_details);
    } catch (error) {
      console.error('Failed to load hardware details:', error);
    }
  };

  const loadSectionData = async (section) => {
    setLoading(true);
    try {
      if (section === 'disks') {
        const res = await api.get(`/agents/${agentId}/metrics/disks?days=30`);
        setDiskData(res.data);
      } else if (section === 'memory') {
        const res = await api.get(`/agents/${agentId}/metrics/memory?days=30`);
        setMemoryData(res.data);
      } else if (section === 'network') {
        const res = await api.get(`/agents/${agentId}/metrics/network?days=30`);
        setNetworkData(res.data);
      } else if (section === 'cpu') {
        const res = await api.get(`/agents/${agentId}/metrics/cpu?days=30`);
        setCpuData(res.data);
      }
    } catch (error) {
      toast.error(`Failed to load ${section} data`);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    loadSectionData(activeSection);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatBps = (bps) => {
    if (bps === 0) return '0 bps';
    const k = 1000;
    const sizes = ['bps', 'Kbps', 'Mbps', 'Gbps'];
    const i = Math.floor(Math.log(bps) / Math.log(k));
    return Math.round(bps / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const menuItems = [
    { id: 'hardware', label: 'Hardware', icon: Info },
    { id: 'cpu', label: 'CPU Usage', icon: Activity },
    { id: 'disks', label: 'Disk Usage', icon: HardDrive },
    { id: 'memory', label: 'Memory', icon: MemoryStick },
    { id: 'network', label: 'Network', icon: Network },
  ];

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  const renderHardwareSection = () => {
    return (
      <div className="space-y-6">
        <HardwareDetailsSection agentId={agentId} />

        {/* Physical Disks - Now handled by HardwareDetailsSection */}
        {hardwareDetails && hardwareDetails.physical_disks && hardwareDetails.physical_disks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="w-5 h-5" />
                Physical Disks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {hardwareDetails.physical_disks.map((disk, idx) => (
                  <div key={idx} className="border-b last:border-0 pb-4 last:pb-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-sm">
                          Disk {disk.index}
                        </Badge>
                        <span className="font-medium">{disk.model}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={disk.media_type?.includes('SSD') ? 'default' : 'secondary'}>
                          {disk.media_type}
                        </Badge>
                        <Badge variant="outline">{disk.partition_style}</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-gray-600">Size:</span>
                        <p className="font-medium">{formatBytes(disk.size_bytes)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Interface:</span>
                        <p className="font-medium">{disk.interface_type}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Status:</span>
                        <p className="font-medium text-green-600">{disk.status}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Partitions:</span>
                        <p className="font-medium">{disk.partitions?.length || 0}</p>
                      </div>
                    </div>
                    
                    {/* Partitions */}
                    {disk.partitions && disk.partitions.length > 0 && (
                      <div className="ml-4 mt-3 space-y-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase">Partitions:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {disk.partitions.map((partition, pIdx) => (
                            <div key={pIdx} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  {partition.drive_letter && (
                                    <Badge variant="outline" className="text-xs">
                                      {partition.drive_letter}
                                    </Badge>
                                  )}
                                  <span className="font-medium">{partition.type}</span>
                                  {partition.bootable && (
                                    <Badge variant="default" className="text-xs">Boot</Badge>
                                  )}
                                </div>
                                <p className="text-gray-600 mt-1">{formatBytes(partition.size_bytes)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Memory Details - Only show on Memory tab */}
        {activeSection === 'memory' && hardwareDetails && hardwareDetails.memory_details && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MemoryStick className="w-5 h-5" />
                Memory Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {hardwareDetails.memory_details.map((module, idx) => (
                  <div key={idx} className="border-b last:border-0 pb-3 last:pb-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Slot {module.device_locator}</span>
                      <Badge variant="outline">{module.memory_type}</Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Capacity:</span>
                        <p className="font-medium">{formatBytes(module.capacity)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Speed:</span>
                        <p className="font-medium">{module.configured_clock_speed} MHz</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Manufacturer:</span>
                        <p className="font-medium">{module.manufacturer}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Part Number:</span>
                        <p className="font-medium font-mono text-xs">{module.part_number}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderCpuSection = () => {
    if (!cpuData) return null;

    return (
      <div className="space-y-6">
        {/* CPU Current Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Current CPU Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <div className="text-sm text-gray-600">Usage</div>
                <div className="text-3xl font-bold text-blue-600">
                  {cpuData.current?.usage_percent?.toFixed(1) || '0.0'}%
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-gray-600">Processor</div>
                <div className="text-sm font-medium">
                  {cpuData.current?.model || 'Unknown'}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm text-gray-600">Cores</div>
                <div className="text-lg font-bold">
                  {cpuData.current?.cores || 'N/A'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CPU Usage History Chart */}
        {cpuData.history && cpuData.history.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  CPU Usage History (Last {cpuData.days || 30} Days)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={cpuData.history}>
                  <defs>
                    <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="timestamp"
                    tickFormatter={(value) => {
                      // Convert to yy/mm/dd format
                      const date = new Date(value);
                      const yy = String(date.getFullYear()).slice(-2);
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      return `${yy}/${mm}/${dd}`;
                    }}
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    tick={{ fontSize: 12 }}
                    label={{ value: 'CPU Usage (%)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    labelFormatter={(value) => {
                      const date = new Date(value);
                      const yy = String(date.getFullYear()).slice(-2);
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      return `${yy}/${mm}/${dd}`;
                    }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="cpu_percent" 
                    stroke="#3b82f6" 
                    fillOpacity={1}
                    fill="url(#colorCpu)"
                    name="CPU Usage (%)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
              <div className="mt-4 text-sm text-gray-500 text-center">
                Data collected every heartbeat. Showing trends over {cpuData.days || 30} days.
              </div>
            </CardContent>
          </Card>
        )}

        {(!cpuData.history || cpuData.history.length === 0) && (
          <Card>
            <CardContent className="py-12 text-center">
              <Activity className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">No historical CPU data available yet</p>
              <p className="text-sm text-gray-500 mt-2">
                Data will be collected over time as the agent sends heartbeats
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderDisksSection = () => {
    if (!diskData) return null;

    return (
      <div className="space-y-6">
        {/* Current Disk Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {diskData.disks?.map((disk, idx) => {
            const usedPercent = ((disk.used / disk.total) * 100).toFixed(1);
            
            return (
              <Card key={disk.drive_letter || idx}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-base">
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4" />
                      {disk.name || disk.mount_point}
                    </div>
                    <Badge variant="outline">{disk.drive_letter}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-medium">{formatBytes(disk.total)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Used:</span>
                      <span className="font-medium text-blue-600">{formatBytes(disk.used)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Free:</span>
                      <span className="font-medium text-green-600">{formatBytes(disk.free)}</span>
                    </div>
                    
                    {/* Usage Bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Usage</span>
                        <span>{usedPercent}% used</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all ${
                            usedPercent > 90 ? 'bg-red-500' : 
                            usedPercent > 75 ? 'bg-orange-500' : 
                            'bg-blue-500'
                          }`}
                          style={{ width: `${usedPercent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Disk Free Space History Chart */}
        {diskData.history && diskData.history.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Disk Free Space History (30 Days)
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Shows remaining free space - Lower values indicate potential disk space issues
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={diskData.history.map(record => {
                  // Ensure free_gb exists (calculate from total - used if needed)
                  const enhanced = { ...record };
                  diskData.disks?.forEach(disk => {
                    const driveKey = disk.mount_point || disk.drive_letter;
                    if (!enhanced[`${driveKey}_free_gb`] && enhanced[`${driveKey}_total_gb`] && enhanced[`${driveKey}_used_gb`]) {
                      enhanced[`${driveKey}_free_gb`] = enhanced[`${driveKey}_total_gb`] - enhanced[`${driveKey}_used_gb`];
                    }
                  });
                  return enhanced;
                })}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="timestamp"
                    tickFormatter={(value) => {
                      // Convert to yy/mm/dd format
                      const date = new Date(value);
                      const yy = String(date.getFullYear()).slice(-2);
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      return `${yy}/${mm}/${dd}`;
                    }}
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis 
                    label={{ value: 'Free (GB)', angle: -90, position: 'insideLeft' }} 
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        // Format date to yy/mm/dd
                        const timestamp = payload[0].payload.timestamp;
                        const date = new Date(timestamp);
                        const yy = String(date.getFullYear()).slice(-2);
                        const mm = String(date.getMonth() + 1).padStart(2, '0');
                        const dd = String(date.getDate()).padStart(2, '0');
                        const formattedDate = `${yy}/${mm}/${dd}`;
                        
                        return (
                          <div className="bg-white p-3 border rounded shadow-lg">
                            <p className="text-sm font-medium mb-2">{formattedDate}</p>
                            {payload.map((entry, index) => {
                              const freeGB = entry.value;
                              const color = freeGB < 5 ? '#ef4444' : freeGB < 10 ? '#f59e0b' : '#10b981';
                              return (
                                <p key={index} className="text-sm" style={{ color }}>
                                  {entry.name}: {freeGB?.toFixed(1)} GB free
                                  {freeGB < 5 && ' ⚠️'}
                                </p>
                              );
                            })}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  {/* Reference lines for thresholds */}
                  <ReferenceLine y={5} stroke="#ef4444" strokeDasharray="3 3" label={{ value: 'Critical (5GB)', position: 'right', fill: '#ef4444', fontSize: 10 }} />
                  <ReferenceLine y={10} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'Warning (10GB)', position: 'right', fill: '#f59e0b', fontSize: 10 }} />
                  
                  {diskData.disks?.map((disk, idx) => {
                    const driveKey = disk.mount_point || disk.drive_letter;
                    // Use blue color for lines - thresholds shown via reference lines and tooltip
                    return (
                      <Line 
                        key={driveKey}
                        type="monotone" 
                        dataKey={`${driveKey}_free_gb`}
                        stroke={COLORS[idx % COLORS.length]}
                        name={`${driveKey} Free Space`}
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
              
              {/* Legend for color coding */}
              <div className="mt-4 flex gap-4 text-xs justify-center">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>Critical (&lt; 5 GB)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span>Warning (&lt; 10 GB)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span>Healthy (≥ 10 GB)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderMemorySection = () => {
    if (!memoryData) return null;

    const memoryUsedPercent = ((memoryData.current?.used / memoryData.current?.total) * 100).toFixed(1);
    const pageFileUsedPercent = memoryData.current?.pagefile_total 
      ? ((memoryData.current.pagefile_used / memoryData.current.pagefile_total) * 100).toFixed(1)
      : 0;

    // Filter out zero/null values for cleaner chart
    const cleanHistory = memoryData.history?.filter(record => 
      record.memory_used_gb > 0 || record.pagefile_used > 0
    ) || [];

    // Check if we have PageFile data
    const hasPageFileData = cleanHistory.some(record => record.pagefile_used > 0);

    // Calculate max value for Y-axis domain
    const maxMemory = Math.max(...cleanHistory.map(r => r.memory_used_gb || 0));
    const maxPageFile = Math.max(...cleanHistory.map(r => r.pagefile_used || 0));
    const maxValue = Math.max(maxMemory, maxPageFile);
    const yAxisMax = Math.ceil(maxValue * 1.1); // Add 10% padding

    return (
      <div className="space-y-6">
        {/* Current Memory Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MemoryStick className="w-5 h-5" />
                Physical Memory
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total:</span>
                  <span className="font-medium">{formatBytes(memoryData.current?.total)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Used:</span>
                  <span className="font-medium">{formatBytes(memoryData.current?.used)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Available:</span>
                  <span className="font-medium text-green-600">{formatBytes(memoryData.current?.available)}</span>
                </div>
                
                {/* Usage Bar */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Usage</span>
                    <span>{memoryUsedPercent}% used</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full ${
                        memoryUsedPercent > 90 ? 'bg-red-500' : 
                        memoryUsedPercent > 75 ? 'bg-orange-500' : 
                        'bg-blue-500'
                      }`}
                      style={{ width: `${memoryUsedPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <HardDrive className="w-5 h-5" />
                Page File
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total:</span>
                  <span className="font-medium">{formatBytes(memoryData.current?.pagefile_total)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Used:</span>
                  <span className="font-medium">{formatBytes(memoryData.current?.pagefile_used)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Available:</span>
                  <span className="font-medium text-green-600">{formatBytes(memoryData.current?.pagefile_available)}</span>
                </div>
                
                {/* Usage Bar */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Usage</span>
                    <span>{pageFileUsedPercent}% used</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full ${
                        pageFileUsedPercent > 90 ? 'bg-red-500' : 
                        pageFileUsedPercent > 75 ? 'bg-orange-500' : 
                        'bg-purple-500'
                      }`}
                      style={{ width: `${pageFileUsedPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Memory Usage History Chart */}
        {cleanHistory.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Memory Usage History (30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={cleanHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="timestamp"
                    tickFormatter={(value) => {
                      // Convert to yy/mm/dd format
                      const date = new Date(value);
                      const yy = String(date.getFullYear()).slice(-2);
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      return `${yy}/${mm}/${dd}`;
                    }}
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis 
                    domain={[0, yAxisMax]}
                    label={{ value: 'Usage (GB)', angle: -90, position: 'insideLeft' }} 
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    formatter={(value) => `${value} GB`}
                    labelFormatter={(value) => {
                      const date = new Date(value);
                      const yy = String(date.getFullYear()).slice(-2);
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      return `${yy}/${mm}/${dd}`;
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="memory_used_gb" 
                    stroke="#3b82f6" 
                    name="Memory Used (GB)" 
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                  {hasPageFileData && (
                    <Line 
                      type="monotone" 
                      dataKey="pagefile_used" 
                      stroke="#8b5cf6" 
                      name="PageFile Used (GB)" 
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
              {cleanHistory.length < memoryData.history?.length && (
                <div className="mt-2 text-xs text-gray-500 text-center">
                  Showing {cleanHistory.length} of {memoryData.history?.length} records (zeros filtered for clarity)
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {cleanHistory.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <MemoryStick className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">No historical memory data available yet</p>
              <p className="text-sm text-gray-500 mt-2">
                Data will be collected over time as the agent sends heartbeats
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderNetworkSection = () => {
    if (!networkData) return null;

    return (
      <div className="space-y-6">
        {/* Network Adapters */}
        <div className="grid grid-cols-1 gap-4">
          {networkData.adapters?.map((adapter, idx) => (
            <Card key={idx}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Network className="w-5 h-5" />
                    {adapter.name}
                  </CardTitle>
                  <Badge variant={adapter.status === 'Up' ? 'default' : 'secondary'}>
                    {adapter.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">IP Address:</span>
                    <p className="font-medium">{adapter.ip_address || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">MAC Address:</span>
                    <p className="font-medium font-mono text-xs">{adapter.mac_address || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Bytes Sent:</span>
                    <p className="font-medium text-blue-600">{formatBytes(adapter.bytes_sent)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Bytes Received:</span>
                    <p className="font-medium text-green-600">{formatBytes(adapter.bytes_received)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Network Usage History Chart */}
        {networkData.history && networkData.history.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Network Usage History (30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={networkData.history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="timestamp"
                    tickFormatter={(value) => {
                      // Convert to yy/mm/dd format
                      const date = new Date(value);
                      const yy = String(date.getFullYear()).slice(-2);
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      return `${yy}/${mm}/${dd}`;
                    }}
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis 
                    tickFormatter={(value) => {
                      // Format Y-axis to show Mbps instead of raw bytes
                      if (value === 0) return '0';
                      if (value < 1) return value.toFixed(2);
                      if (value < 10) return value.toFixed(1);
                      return Math.round(value).toString();
                    }}
                    label={{ value: 'Bandwidth (Mbps)', angle: -90, position: 'insideLeft' }} 
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    formatter={(value) => `${value.toFixed(2)} Mbps`}
                    labelFormatter={(value) => {
                      const date = new Date(value);
                      const yy = String(date.getFullYear()).slice(-2);
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      return `${yy}/${mm}/${dd}`;
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="bandwidth_mbps" 
                    stroke="#3b82f6" 
                    name="Network Bandwidth" 
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      {/* Left Sidebar Menu - Stacks on mobile, sidebar on desktop */}
      <div className="w-full lg:w-64 flex-shrink-0">
        <Card>
          <CardHeader className="pb-3 lg:pb-6">
            <CardTitle className="text-base">System Info</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <nav className="flex lg:flex-col lg:space-y-1 overflow-x-auto lg:overflow-x-visible">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`flex-shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-3 text-left transition-colors whitespace-nowrap ${
                      activeSection === item.id
                        ? 'bg-blue-50 text-blue-700 border-b-4 lg:border-b-0 lg:border-r-4 border-blue-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </CardContent>
        </Card>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">
            {menuItems.find(item => item.id === activeSection)?.label}
          </h2>
          <Button
            onClick={refreshData}
            variant="outline"
            size="sm"
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Content Sections */}
        <div className="space-y-6">
          {loading && !cpuData && !diskData && !memoryData && !networkData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {activeSection === 'hardware' && renderHardwareSection()}
              {activeSection === 'cpu' && renderCpuSection()}
              {activeSection === 'disks' && renderDisksSection()}
              {activeSection === 'memory' && renderMemorySection()}
              {activeSection === 'network' && renderNetworkSection()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoTab;